import { forwardRef, useMemo, useState } from "react";

import { ChevronsRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

import CreateBillingPlanForm from "../add-billing-plan";
import { AdminDeclineOpportunityForm } from "../provide-opt-out-reason";

import { AdminJobCandidateDetails } from "@/queries/admin/job-details";
import { useSetCandidateOpportunityStatus } from "@/mutations/admin/set-candidate-opportunity-status";

type BaseButtonProps = React.ComponentPropsWithoutRef<typeof Button>;

interface UpdateCandidateButtonProps extends BaseButtonProps {
  jobId: string;
  candidate: AdminJobCandidateDetails;
  onSuccess?: (data: AdminJobCandidateDetails) => void;
}

export const AdvanceCandidateOpportunityStatusButton = forwardRef<
  HTMLButtonElement,
  UpdateCandidateButtonProps
>(({ jobId, candidate, onSuccess, ...buttonProps }, ref) => {
  const [open, setOpen] = useState(false);

  const successFn = useMemo(
    () => (data: AdminJobCandidateDetails) => {
      setOpen(false);
      onSuccess?.(data);
    },
    [setOpen, onSuccess],
  );

  const { setCandidateOpportunityStatus, isSettingCandidateOpportunityStatus } =
    useSetCandidateOpportunityStatus({ openingId: jobId });

  const handleClick = () => {
    if (candidate.opportunityStatus === "Contracting") {
      setOpen(true);
    } else {
      setCandidateOpportunityStatus({
        statusId: candidate.candidateStatusId,
        currentStatus: candidate.opportunityStatus,
      });
    }
  };

  if (
    candidate.opportunityStatus === "Accepted by Client" ||
    candidate.opportunityStatus === "Not Interested" ||
    candidate.opportunityStatus === "Rejected for Role"
  ) {
    return;
  }

  return (
    <>
      <Button
        {...buttonProps}
        ref={ref}
        variant="secondary"
        disabled={isSettingCandidateOpportunityStatus}
        onClick={handleClick}
      >
        Advance <ChevronsRight />
      </Button>
      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="w-full sm:max-w-2xl">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <DialogTitle>Create Billing Plan</DialogTitle>
              </div>
              <DialogDescription className="sr-only">
                Create a billing plan for a candidate and role
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
              <CreateBillingPlanForm
                jobOpeningId={jobId}
                candidate={candidate}
                onCancel={() => setOpen(false)}
                onSuccess={successFn}
              />
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
});
AdvanceCandidateOpportunityStatusButton.displayName =
  "AdvanceCandidateOpportunityStatusButton";

export const DeclineCandidateOpportunityStatusButton = forwardRef<
  HTMLButtonElement,
  UpdateCandidateButtonProps
>(({ jobId, candidate, onSuccess, ...buttonProps }, ref) => {
  const [open, setOpen] = useState(false);

  const successFn = useMemo(
    () => (data: AdminJobCandidateDetails) => {
      setOpen(false);
      onSuccess?.(data);
    },
    [setOpen, onSuccess],
  );

  const { setCandidateOpportunityStatus, isSettingCandidateOpportunityStatus } =
    useSetCandidateOpportunityStatus({ openingId: jobId });

  const handleClick = () => {
    if (candidate.opportunityStatus === "Checking Interest") {
      setOpen(true);
    } else {
      setCandidateOpportunityStatus({
        statusId: candidate.candidateStatusId,
        status: "Rejected for Role",
      });
    }
  };

  if (
    candidate.opportunityStatus === "Accepted by Client" ||
    candidate.opportunityStatus === "Not Interested" ||
    candidate.opportunityStatus === "Rejected for Role"
  ) {
    return;
  }

  return (
    <>
      <Button
        {...buttonProps}
        ref={ref}
        variant="destructive"
        disabled={isSettingCandidateOpportunityStatus}
        onClick={handleClick}
      >
        Decline
      </Button>
      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="w-full sm:max-w-2xl">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <DialogTitle>Decline candidate</DialogTitle>
              </div>
              <DialogDescription className="sr-only">
                Please indicate whether the candidate opted out or is being
                rejected by admin
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
              <AdminDeclineOpportunityForm
                openingId={jobId}
                candidateStatusId={candidate.candidateStatusId}
                onCancel={() => setOpen(false)}
                onSuccess={successFn}
              />
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
});
DeclineCandidateOpportunityStatusButton.displayName =
  "DeclineCandidateOpportunityStatusButton";
